/**
 * Shared app-search engine (integrations-page UX overhaul, 2026-08-18).
 *
 * ONE scored matcher for every surface that resolves a typed app name against
 * the Composio catalog: the Integrations page omnisearch, the "connect any
 * system" lane, and the in-chat connect-app resolver. Promotes the chat
 * resolver's proven ladder (exact → keyword → contains) and extends it with
 * prefix and bounded-edit-distance tiers so "gmial", "outlok", and "email"
 * all land on the right tiles instead of falling through to a custom
 * registration.
 *
 * Scoring tiers (lower = better):
 *   0 exact     — slug/name equality (raw, whitespace-collapsed, underscored)
 *   1 synonym   — word-boundary hit in SEARCH_SYNONYMS ("email" → gmail)
 *   2 prefix    — a name/slug word starts with the query ("outl" → Outlook)
 *   3 substring — bidirectional contains (the old fuzzy tier)
 *   4 fuzzy     — Levenshtein ≤ 1 (short names) / ≤ 2 (longer), typo rescue
 */

import { sortByLikelihood } from './ComposioClient';
import { connectLaneFor, laneIsDirect } from './connectLane';
import type { ComposioToolkit } from './ComposioClient';

export type MatchReason = 'exact' | 'synonym' | 'prefix' | 'substring' | 'fuzzy';

export interface ScoredToolkit {
  tk: ComposioToolkit;
  /** Lower = better. Equals the tier that produced the match. */
  score: number;
  reason: MatchReason;
}

const REASON_BY_TIER: MatchReason[] = ['exact', 'synonym', 'prefix', 'substring', 'fuzzy'];

// ============================================================================
// Synonyms
// ============================================================================

/**
 * Keyword/synonym → toolkit slug map. Keys are REAL catalog slugs (verified
 * against the curated lists — never invent a slug). Superset of the old
 * connect-app APP_KEYWORD_MAP: same entries plus the everyday vocabulary
 * organizations actually type ("email", "e-sign", "microsoft 365", …).
 * Matching is word-boundary, never bare substring — 'box' must not fire on
 * "inbox"/"dropbox" (review finding, 2026-08-11).
 */
export const SEARCH_SYNONYMS: Record<string, string[]> = {
  gmail: ['gmail', 'google mail', 'email', 'e-mail', 'mail', 'correo'],
  outlook: ['outlook', 'email', 'e-mail', 'mail', 'microsoft mail', 'microsoft 365', 'office 365', 'hotmail'],
  googlecalendar: ['google calendar', 'calendar', 'calendario', 'meeting', 'appointment', 'scheduling'],
  calendly: ['calendly', 'scheduling link', 'booking'],
  googledrive: ['google drive', 'drive', 'files', 'storage', 'cloud storage'],
  googledocs: ['google docs', 'google doc', 'docs', 'documents'],
  googlesheets: ['google sheets', 'sheets', 'spreadsheet', 'spreadsheets', 'excel'],
  googlecontacts: ['google contacts', 'contacts'],
  slack: ['slack'],
  microsoft_teams: ['teams', 'microsoft teams', 'ms teams', 'microsoft 365', 'office 365'],
  one_drive: ['onedrive', 'one drive', 'microsoft 365', 'office 365', 'files', 'storage'],
  dropbox: ['dropbox', 'files', 'storage'],
  box: ['box', 'files', 'storage'],
  notion: ['notion', 'wiki', 'notes'],
  airtable: ['airtable'],
  hubspot: ['hubspot', 'crm'],
  salesforce: ['salesforce', 'crm'],
  zendesk: ['zendesk', 'crm', 'helpdesk', 'support tickets'],
  github: ['github', 'git'],
  gitlab: ['gitlab', 'git'],
  linear: ['linear'],
  jira: ['jira'],
  asana: ['asana'],
  trello: ['trello'],
  monday: ['monday'],
  clickup: ['clickup'],
  todoist: ['todoist'],
  basecamp: ['basecamp'],
  zoom: ['zoom', 'video call', 'video conferencing'],
  whatsapp: ['whatsapp', 'whats app'],
  intercom: ['intercom'],
  stripe: ['stripe', 'payments'],
  quickbooks: ['quickbooks', 'quick books', 'bookkeeping', 'accounting'],
  freshbooks: ['freshbooks', 'accounting'],
  mailchimp: ['mailchimp', 'newsletter'],
  linkedin: ['linkedin', 'linked in'],
  youtube: ['youtube', 'you tube', 'yt', 'video', 'videos', 'channel', 'vlog', 'shorts'],
  typeform: ['typeform', 'forms', 'survey', 'surveys'],
  docusign: ['docusign', 'e-sign', 'esign', 'esignature', 'e-signature', 'signature', 'signatures', 'sign documents'],
  miro: ['miro', 'whiteboard'],
  figma: ['figma'],
  canva: ['canva'],
};

/**
 * Back-compat alias: connect-app's intent map moved here (2026-08-18). Same
 * object — one source of truth for "what the user might call this app".
 */
export const APP_KEYWORD_MAP = SEARCH_SYNONYMS;

// ============================================================================
// First actions (the wow moment)
// ============================================================================

/**
 * Per-app suggested FIRST action, offered right after a connect completes —
 * one click opens chat with this prompt seeded. Written as the user's own
 * words. Fallback for unmapped apps: firstActionForApp composes a generic
 * "what can you do with <app>?".
 */
export const FIRST_ACTION_MAP: Record<string, string> = {
  gmail: 'summarize my unread email',
  outlook: 'summarize my unread email',
  googlecalendar: "what's on my calendar this week?",
  calendly: 'what bookings do I have coming up?',
  googledrive: 'what are my most recent documents?',
  googledocs: 'list my most recent documents',
  googlesheets: 'list my most recent spreadsheets',
  googlecontacts: 'look up a contact for me',
  slack: "summarize what I've missed in Slack",
  microsoft_teams: "summarize what I've missed in Teams",
  one_drive: 'what are my most recent files?',
  dropbox: 'what are my most recent files?',
  box: 'what are my most recent files?',
  notion: 'what pages did we update recently?',
  hubspot: 'summarize my open deals',
  salesforce: 'summarize my open opportunities',
  zendesk: 'summarize my open tickets',
  stripe: 'how did payments look this week?',
  quickbooks: 'summarize unpaid invoices',
  docusign: 'which envelopes are still waiting for signature?',
  github: 'what pull requests need my attention?',
  jira: "what's assigned to me right now?",
  linear: "what's assigned to me right now?",
  asana: "what's assigned to me right now?",
  trello: "what's on my board?",
  whatsapp: 'summarize my recent WhatsApp messages',
  zoom: 'what meetings do I have coming up?',
  mailchimp: 'how did my last campaign perform?',
  typeform: 'summarize my latest form responses',
  youtube: 'list the videos on my channel',
};

/** The one-click "try it now" prompt for a just-connected app. */
export function firstActionForApp(slug: string, displayName?: string): string {
  const mapped = FIRST_ACTION_MAP[String(slug || '').toLowerCase()];
  if (mapped) return mapped;
  const name = (displayName || slug || 'this app').trim();
  return `what can you do with ${name}?`;
}

// ============================================================================
// Edit distance (typo rescue)
// ============================================================================

/**
 * Bounded OSA (optimal string alignment) distance: true when the edit
 * distance — counting adjacent TRANSPOSITIONS as one edit, the most common
 * real typo ("gmial") — is <= max. Early-exits on the length gap and on any
 * all-above-cap row; cheap enough to run across the whole ~1,200-toolkit
 * catalog per keystroke.
 */
export function editDistanceWithin(a: string, b: string, max: number): boolean {
  if (a === b) return true;
  const la = a.length;
  const lb = b.length;
  if (Math.abs(la - lb) > max) return false;
  if (la === 0 || lb === 0) return Math.max(la, lb) <= max;

  let prevPrev = new Array<number>(lb + 1);
  let prev = new Array<number>(lb + 1);
  let curr = new Array<number>(lb + 1);
  for (let j = 0; j <= lb; j++) prev[j] = j;

  for (let i = 1; i <= la; i++) {
    curr[0] = i;
    let rowMin = curr[0];
    const ca = a.charCodeAt(i - 1);
    for (let j = 1; j <= lb; j++) {
      const cost = ca === b.charCodeAt(j - 1) ? 0 : 1;
      let d = Math.min(prev[j] + 1, curr[j - 1] + 1, prev[j - 1] + cost);
      if (
        i > 1 && j > 1 &&
        ca === b.charCodeAt(j - 2) &&
        a.charCodeAt(i - 2) === b.charCodeAt(j - 1)
      ) {
        d = Math.min(d, prevPrev[j - 2] + 1); // transposition
      }
      curr[j] = d;
      if (d < rowMin) rowMin = d;
    }
    if (rowMin > max) return false; // whole row above the cap — no way back
    const rotate = prevPrev; prevPrev = prev; prev = curr; curr = rotate;
  }
  return prev[lb] <= max;
}

// ============================================================================
// The matcher
// ============================================================================

function escapeRegExp(text: string): string {
  return text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/** Word-boundary test that tolerates multi-word keywords ("google drive"). */
function wordBoundaryHit(haystack: string, keyword: string): boolean {
  if (haystack === keyword) return true;
  const re = new RegExp(`(^|[^a-z0-9])${escapeRegExp(keyword)}($|[^a-z0-9])`, 'i');
  return re.test(haystack);
}

export interface SearchToolkitsOptions {
  /** Cap the returned list (applied after ranking). Default: unlimited. */
  limit?: number;
}

/**
 * Rank the catalog against a typed query. Returns every match, best first:
 * score (tier) → genuinely-one-click first → curated likelihood → tool count
 * → name. Empty/too-short queries return [].
 */
export function searchToolkits(
  query: string,
  toolkits: ComposioToolkit[],
  opts?: SearchToolkitsOptions
): ScoredToolkit[] {
  const normalized = (query || '').trim().toLowerCase();
  if (!normalized || toolkits.length === 0) return [];

  const collapsed = normalized.replace(/\s+/g, '');
  const underscored = normalized.replace(/\s+/g, '_');

  const bySlug = new Map<string, ComposioToolkit>();
  for (const tk of toolkits) {
    if (tk?.slug) bySlug.set(tk.slug.toLowerCase(), tk);
  }

  const scored = new Map<string, ScoredToolkit>();
  const add = (tk: ComposioToolkit | undefined, tier: number) => {
    if (!tk?.slug) return;
    const key = tk.slug.toLowerCase();
    const existing = scored.get(key);
    if (!existing || tier < existing.score) {
      scored.set(key, { tk, score: tier, reason: REASON_BY_TIER[tier] });
    }
  };

  // Tier 0 — exact slug / name.
  add(bySlug.get(normalized), 0);
  add(bySlug.get(collapsed), 0);
  add(bySlug.get(underscored), 0);
  for (const tk of toolkits) {
    const name = (tk.name || '').toLowerCase();
    if (name && (name === normalized || name === collapsed)) add(tk, 0);
  }

  // Tier 1 — synonym / intent keyword (word-boundary, never bare substring).
  for (const [slug, keywords] of Object.entries(SEARCH_SYNONYMS)) {
    const tk = bySlug.get(slug);
    if (!tk) continue;
    for (const keyword of keywords) {
      if (wordBoundaryHit(normalized, keyword)) {
        add(tk, 1);
        break;
      }
    }
  }

  // Tier 2 — prefix: a word of the name (or the slug) starts with the query.
  if (normalized.length >= 2) {
    for (const tk of toolkits) {
      const name = (tk.name || '').toLowerCase();
      const slug = (tk.slug || '').toLowerCase();
      if (
        slug.startsWith(collapsed) ||
        name.startsWith(normalized) ||
        name.split(/[^a-z0-9]+/).some((word) => word && word.startsWith(collapsed))
      ) {
        add(tk, 2);
      }
    }
  }

  // Tier 3 — substring, reasonably specific input only so one letter never
  // matches half the catalog. Forward direction (query inside name/slug) is
  // raw contains — "drive" must find OneDrive. Reverse direction (name inside
  // the query) is WORD-BOUNDARY: "inbox" must never surface Box (the
  // 2026-08-11 regression, generalized).
  if (normalized.length >= 3 && normalized.length <= 30) {
    for (const tk of toolkits) {
      const name = (tk.name || '').toLowerCase();
      const slug = (tk.slug || '').toLowerCase();
      if (
        name.includes(normalized) ||
        slug.includes(collapsed) ||
        (name.length >= 3 && wordBoundaryHit(normalized, name))
      ) {
        add(tk, 3);
      }
    }
  }

  // Tier 4 — typo rescue. Only for queries long enough that an edit distance
  // means "typo" rather than "different word".
  if (normalized.length >= 4) {
    const maxEdits = normalized.length <= 6 ? 1 : 2;
    for (const tk of toolkits) {
      const key = (tk.slug || '').toLowerCase();
      if (scored.has(key)) continue; // already matched a better tier
      const name = (tk.name || '').toLowerCase();
      if (
        editDistanceWithin(collapsed, key, maxEdits) ||
        (name && editDistanceWithin(normalized, name, maxEdits))
      ) {
        add(tk, 4);
      }
    }
  }

  const ranked = Array.from(scored.values()).sort((a, b) => {
    if (a.score !== b.score) return a.score - b.score;
    const aClick = connectLaneFor(a.tk) === 'oauth' ? 0 : 1;
    const bClick = connectLaneFor(b.tk) === 'oauth' ? 0 : 1;
    if (aClick !== bClick) return aClick - bClick;
    const curated = sortByLikelihood(a.tk, b.tk);
    if (curated !== 0) return curated;
    return (b.tk.meta?.tools_count || 0) - (a.tk.meta?.tools_count || 0);
  });

  return opts?.limit ? ranked.slice(0, opts.limit) : ranked;
}

/**
 * Chat-resolver view of the same ranking (connect-app cards): keeps the old
 * semantics — exact/synonym hits are authoritative and suppress the broader
 * tiers; prefix/substring/fuzzy only surface when nothing better matched.
 * Ordering preserves the historical card sort: genuinely available one-click
 * OAuth first, then tier, then tool count.
 */
export function searchToolkitsForResolver(
  query: string,
  toolkits: ComposioToolkit[],
  limit: number = 3
): ScoredToolkit[] {
  const ranked = searchToolkits(query, toolkits);
  if (ranked.length === 0) return [];
  const best = ranked[0].score;
  const gated = best <= 1 ? ranked.filter((r) => r.score <= 1) : ranked;
  return gated
    .slice() // stable copy before re-sorting with the card comparator
    .sort((a, b) => {
      const aOneClick = laneIsDirect(connectLaneFor(a.tk)) ? 0 : 1;
      const bOneClick = laneIsDirect(connectLaneFor(b.tk)) ? 0 : 1;
      if (aOneClick !== bOneClick) return aOneClick - bOneClick;
      if (a.score !== b.score) return a.score - b.score;
      return (b.tk.meta?.tools_count || 0) - (a.tk.meta?.tools_count || 0);
    })
    .slice(0, limit);
}
